<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';

export default {
  components: {
    ChatList,
    ConversationBox,
    CmdBarConversationSnooze,
    SidepanelSwitch,
    ConversationSidebar,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();

    return {
      uiSettings,
      updateUISettings,
      accountId,
    };
  },
  data() {
    return {
      showSearchModal: false,
      conversationListWidth: 360,
      conversationSidebarWidth: 420,
      activeResize: null,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
    }),
    showConversationList() {
      return this.isOnExpandedLayout ? !this.conversationId : true;
    },
    showMessageView() {
      return this.conversationId ? true : !this.isOnExpandedLayout;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },

    shouldShowSidebar() {
      if (!this.currentChat.id) {
        return false;
      }

      const { is_contact_sidebar_open: isContactSidebarOpen } = this.uiSettings;
      return isContactSidebarOpen;
    },
  },
  watch: {
    conversationId() {
      this.fetchConversationIfUnavailable();
    },
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('portals/index');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
  },
  beforeUnmount() {
    this.stopResize();
  },

  methods: {
    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    },
    startResize(panel, event) {
      if (event.pointerType === 'touch') {
        return;
      }

      this.activeResize = {
        panel,
        startX: event.clientX,
        startConversationListWidth: this.conversationListWidth,
        startConversationSidebarWidth: this.conversationSidebarWidth,
      };
      document.body.classList.add('select-none');
      window.addEventListener('pointermove', this.onResize);
      window.addEventListener('pointerup', this.stopResize, { once: true });
    },
    onResize(event) {
      if (!this.activeResize) {
        return;
      }

      const delta = event.clientX - this.activeResize.startX;
      const maxPanelWidth = Math.max(320, window.innerWidth - 520);

      if (this.activeResize.panel === 'conversation-list') {
        this.conversationListWidth = this.clamp(
          this.activeResize.startConversationListWidth + delta,
          280,
          maxPanelWidth
        );
        return;
      }

      this.conversationSidebarWidth = this.clamp(
        this.activeResize.startConversationSidebarWidth - delta,
        320,
        maxPanelWidth
      );
    },
    stopResize() {
      this.activeResize = null;
      document.body.classList.remove('select-none');
      window.removeEventListener('pointermove', this.onResize);
    },
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      if (!chat) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
  },
};
</script>

<template>
  <section class="flex w-full h-full min-w-0">
    <div
      v-show="showConversationList"
      class="flex h-full min-w-0 shrink-0"
      :style="{
        width: showMessageView ? `${conversationListWidth}px` : '100%',
      }"
    >
      <ChatList
        class="!w-full 2xl:!w-full !basis-auto"
        :show-conversation-list="showConversationList"
        :conversation-inbox="inboxId"
        :label="label"
        :team-id="teamId"
        :conversation-type="conversationType"
        :folders-id="foldersId"
        :is-on-expanded-layout="isOnExpandedLayout"
        @conversation-load="onConversationLoad"
      />
    </div>
    <div
      v-if="showConversationList"
      class="conversation-resize-boundary group"
      @pointerdown="startResize('conversation-list', $event)"
    >
      <span class="conversation-resize-handle">
        <span />
        <span />
        <span />
      </span>
    </div>
    <ConversationBox
      v-if="showMessageView"
      class="flex-1"
      :inbox-id="inboxId"
      :is-on-expanded-layout="isOnExpandedLayout"
    >
      <SidepanelSwitch v-if="currentChat.id" />
    </ConversationBox>
    <div
      v-if="shouldShowSidebar"
      class="conversation-resize-boundary conversation-sidebar-resize-boundary group"
      @pointerdown="startResize('conversation-sidebar', $event)"
    >
      <span class="conversation-resize-handle">
        <span />
        <span />
        <span />
      </span>
    </div>
    <ConversationSidebar
      v-if="shouldShowSidebar"
      :current-chat="currentChat"
      :width="conversationSidebarWidth"
    />
    <CmdBarConversationSnooze />
  </section>
</template>

<style scoped>
.conversation-resize-boundary {
  @apply hidden md:flex relative z-20 w-1 shrink-0 items-center justify-center border-l border-r border-n-weak bg-transparent transition-colors;
}

.conversation-resize-boundary:hover {
  @apply bg-n-brand/20;
  cursor: pointer !important;
}

.conversation-resize-boundary:hover * {
  cursor: pointer !important;
}

.conversation-resize-handle {
  @apply pointer-events-auto absolute left-1/2 top-1/2 z-30 flex h-10 w-4 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-1 rounded-full border border-n-weak bg-white shadow-sm opacity-80 transition-opacity group-hover:opacity-100 dark:bg-n-slate-2;
}

.conversation-resize-handle span {
  @apply block h-0.5 w-2 rounded-full bg-n-slate-7;
}

.conversation-sidebar-resize-boundary {
  @apply z-[80];
}

.conversation-sidebar-resize-boundary .conversation-resize-handle {
  @apply z-[90];
}
</style>
